import { SentSuggestionSchema } from '@/schemas/api/ai/suggest-price';
import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';
import {
  idValidator,
  priceValidatorCoerce,
  latitudeValidatorCoerceNullable,
  longitudeValidatorCoerceNullable,
  idValidatorNonNegativeOptional,
} from '@/schemas/common/validators';

/**
 * Schema for presigned URL objects returned when photos need to be uploaded
 */
export const PresignedUrlSchema = z.object({
  /** Index of the photo (0-based) */
  photoIndex: z.number(),
  /** Size variant (currently only 'original' as Lambda auto-generates others) */
  size: z.string(),
  /** Presigned S3 URL for uploading */
  presignedUrl: z.string(),
});

/**
 * An optional price where a blank form field means "not given".
 *
 * The sell form starts floorPrice and recommendedPrice as '' and sends them
 * that way. z.coerce.number() reads '' (and null) as 0, so every listing whose
 * seller left them blank was stored with a $0 floor and a $0 recommended price,
 * and the AI suggestion's recommended price was thrown away (`0 ?? suggestion`
 * keeps the 0). Prod, 60 days to 2026-10-31: 512 seller listings at floorPrice
 * 0, 510 at recommendedPrice 0, none with a positive floor.
 */
export const blankPriceAsAbsent = z.preprocess(
  (value: unknown) => (value === '' || value === null ? undefined : value),
  priceValidatorCoerce.optional()
);

/**
 * Request schema for POST /api/listings/create
 * Creates a new listing with optional presigned URLs for photo uploads
 */
export const CreateListingRequestSchema = z.object({
  /** Listing price in dollars */
  price: priceValidatorCoerce.max(999999, 'Price must be less than 1,000,000'),
  /** Listing title */
  title: z.string().min(3, 'Title must be at least 3 characters').max(255, 'Title must be at most 255 characters'),
  /** Listing description text */
  text: z.string().optional(),
  /** Vehicle identification number (for auto listings) */
  vin: z.union([z.string(), z.number()]).optional(),
  /** Vehicle mileage (for auto listings) */
  mileage: z.union([z.string(), z.number()]).optional(),
  /**
   * Location latitude. Null is "not given": z.coerce.number() alone read a JSON
   * null as 0. The route still refuses a 0 pair and places the listing at the seller.
   */
  latitude: latitudeValidatorCoerceNullable.optional(),
  /** Location longitude; null as for latitude. */
  longitude: longitudeValidatorCoerceNullable.optional(),
  /** Ignored: the town is derived from where the listing ships from (utils/listing-location-text.ts). */
  locationText: z.string().optional(),
  /** Which of the seller's saved addresses it ships from; absent or null is their default. */
  addressId: z.coerce.number().int().positive().nullable().optional(),
  /** Item condition (1-5 scale) */
  itemCondition: idValidatorNonNegativeOptional,
  /** Whether the item can be shipped */
  shippable: z.union([z.boolean(), z.coerce.number()]).optional(),
  /** Category ID for the listing */
  categoryId: idValidator,
  /** Item height in inches */
  height: z.union([z.number(), z.string()]).optional(),
  /** Item width in inches */
  width: z.union([z.number(), z.string()]).optional(),
  /** Item weight in pounds */
  weight: z.union([z.number(), z.string()]).optional(),
  /** Item depth in inches */
  depth: z.union([z.number(), z.string()]).optional(),
  /** Shippo USPS parcel template token the seller chose; empty or unknown means custom dimensions */
  parcelTemplate: z.string().max(40).nullable().optional(),
  /** Number of photos to upload */
  photos: z.coerce.number().optional(),
  /** Floor price - minimum price seller will accept. Blank is no floor. */
  floorPrice: blankPriceAsAbsent,
  /** System-recommended price. Blank leaves it to the AI suggestion. */
  recommendedPrice: blankPriceAsAbsent,
  /**
   * The sell page's AI price suggestion, when the seller asked for one
   * (utils/price-suggestion-signal.ts). Kept as the listing's estimate when
   * the listing is priced at it.
   */
  priceSuggestion: SentSuggestionSchema.optional(),
});

/**
 * Success response data for created listing
 */
export const CreateListingSuccessSchema = z.object({
  success: z.literal(true),
  listing: z.object({
    /** Database ID of the created listing */
    id: z.number(),
    /** Listing title */
    title: z.string(),
    /** Listing price as string (for precision) */
    price: z.string(),
  }),
  /** Presigned URLs for photo uploads (if photos > 0) */
  presignedUrls: z.array(PresignedUrlSchema).optional(),
  /**
   * True when the listing is held for an admin to review before it goes live
   * (a phone number, a WhatsApp/Telegram contact or a supplier pitch in its
   * text; utils/listing-spam.ts). The seller is told it is under review.
   */
  underReview: z.boolean().optional(),
});

/**
 * Response schema for POST /api/listings/create
 * Returns created listing data with optional presigned URLs for photo uploads
 */
export const CreateListingResponseSchema = z.discriminatedUnion('success', [
  CreateListingSuccessSchema,
  ErrorResponseSchema,
]);

export type CreateListingRequest = z.infer<typeof CreateListingRequestSchema>;
export type CreateListingResponse = z.infer<typeof CreateListingResponseSchema>;
export type PresignedUrl = z.infer<typeof PresignedUrlSchema>;
